import { graphql } from './gql';

/**
 * A month: arqam's totals (doc 55: transfers never count, loan money only
 * when asked), against the month before, the work left, and the month's
 * transfers, loan money and in-kind contributions.
 */
export const MonthDocument = graphql(`
  query DeskMonth(
    $from: Date!
    $to: Date!
    $prevFrom: Date!
    $prevTo: Date!
  ) {
    total: financeTotals(dateFrom: $from, dateTo: $to, groupBy: "none") {
      nodes {
        income
        expenses
        result
        count
      }
    }
    previous: financeTotals(
      dateFrom: $prevFrom
      dateTo: $prevTo
      groupBy: "none"
    ) {
      nodes {
        income
        expenses
        result
      }
    }
    byCategory: financeTotals(
      dateFrom: $from
      dateTo: $to
      groupBy: "category"
    ) {
      nodes {
        key
        label
        income
        expenses
        count
      }
    }
    byProject: financeTotals(dateFrom: $from, dateTo: $to, groupBy: "project") {
      nodes {
        key
        label
        income
        expenses
        result
      }
    }
    byAccount: financeTotals(dateFrom: $from, dateTo: $to, groupBy: "account") {
      nodes {
        key
        label
        income
        expenses
        result
      }
    }
    all: financialOperations(
      filter: { date: { greaterThanOrEqualTo: $from, lessThanOrEqualTo: $to } }
    ) {
      totalCount
    }
    review: financialOperations(
      filter: {
        date: { greaterThanOrEqualTo: $from, lessThanOrEqualTo: $to }
        needsReview: { equalTo: true }
        transferId: { isNull: true }
      }
    ) {
      totalCount
    }
    noCategory: financialOperations(
      filter: {
        date: { greaterThanOrEqualTo: $from, lessThanOrEqualTo: $to }
        categoryExists: false
        transferId: { isNull: true }
      }
    ) {
      totalCount
    }
    noCounterparty: financialOperations(
      filter: {
        date: { greaterThanOrEqualTo: $from, lessThanOrEqualTo: $to }
        counterpartyExists: false
        transferId: { isNull: true }
      }
    ) {
      totalCount
    }
    noReceipt: financialOperations(
      filter: {
        date: { greaterThanOrEqualTo: $from, lessThanOrEqualTo: $to }
        type: { equalTo: debit }
        receiptPhotoExists: false
        transferId: { isNull: true }
      }
    ) {
      totalCount
    }
    loans: financialOperations(
      filter: {
        date: { greaterThanOrEqualTo: $from, lessThanOrEqualTo: $to }
        debtId: { isNull: false }
      }
      orderBy: [DATE_ASC]
    ) {
      nodes {
        id
        date
        type
        amount
        debtId
        description
      }
    }
    accountTransfers(
      filter: { date: { greaterThanOrEqualTo: $from, lessThanOrEqualTo: $to } }
      orderBy: [DATE_ASC]
    ) {
      nodes {
        id
        date
        amount
        description
        fromAccount {
          name
        }
        toAccount {
          name
        }
      }
    }
    inKindContributions(
      filter: { date: { greaterThanOrEqualTo: $from, lessThanOrEqualTo: $to } }
      orderBy: [DATE_ASC]
    ) {
      nodes {
        id
        date
        description
        estimatedValue
        counterparty {
          name
        }
      }
    }
  }
`);

/** The cockpit: the year (total and by month), the work left, open loans,
 * what was entered last. Balances come from the shared lists. */
export const CockpitDocument = graphql(`
  query DeskCockpit($yearFrom: Date!, $yearTo: Date!) {
    year: financeTotals(
      dateFrom: $yearFrom
      dateTo: $yearTo
      groupBy: "none"
    ) {
      nodes {
        income
        expenses
        result
      }
    }
    months: financeTotals(
      dateFrom: $yearFrom
      dateTo: $yearTo
      groupBy: "month"
    ) {
      nodes {
        key
        income
        expenses
      }
    }
    review: financeReviewQueue {
      totalCount
    }
    noCategory: financialOperations(
      filter: { categoryExists: false, transferId: { isNull: true } }
    ) {
      totalCount
    }
    noCounterparty: financialOperations(
      filter: { counterpartyExists: false, transferId: { isNull: true } }
    ) {
      totalCount
    }
    noReceipt: financialOperations(
      filter: {
        type: { equalTo: debit }
        receiptPhotoExists: false
        transferId: { isNull: true }
      }
    ) {
      totalCount
    }
    debts(
      filter: { outstanding: { notEqualTo: "0" } }
      orderBy: [DATE_DESC]
    ) {
      nodes {
        id
        direction
        outstanding
        purpose
        counterparty {
          name
        }
      }
    }
    recent: financialOperations(orderBy: [CREATED_AT_DESC], first: 8) {
      nodes {
        id
        date
        type
        amount
        description
        transferId
        category {
          name
        }
      }
    }
  }
`);
